/**
 * Render failures on `/chats/:token` stay on that URL.
 *
 * Recovering via `backHref` or `history.back()` is what lands the user on
 * Results with no send. Retry remounts the chat in place.
 */

import { Component, ReactNode } from 'react'
import { AlertCircle } from '@digital-twins/design-system/icons'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { Button } from '@/components/ui/button'
import { PageLayout } from '@/components/layout/PageLayout'
import i18n from '@/i18n'
import { pathnameOf, readChatCrashRestore, reportChatPageCrash } from './chatCrashRestore'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  resetKey: number
}

function roomTokenFromLocation(): string | null {
  if (typeof window === 'undefined') return null
  const path = window.location.pathname
  const marker = '/chats/'
  const index = path.indexOf(marker)
  if (index === -1) return null
  const token = path.slice(index + marker.length).split('/')[0]
  if (!token) return null
  try {
    return decodeURIComponent(token)
  } catch {
    return token
  }
}

export class ChatPageErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, resetKey: 0 }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true }
  }

  componentDidCatch(): void {
    const record = readChatCrashRestore()
    const roomToken = roomTokenFromLocation()
    const studyId =
      record && roomToken && (record.roomToken === roomToken || pathnameOf(record.href).endsWith(`/${roomToken}`))
        ? record.studyId
        : record?.studyId ?? null
    reportChatPageCrash({
      source: 'react',
      roomToken: roomToken ?? record?.roomToken ?? null,
      studyId,
      sendStarted: record?.sendStarted === true,
    })
  }

  private handleRetry = () => {
    this.setState((prev) => ({
      hasError: false,
      resetKey: prev.resetKey + 1,
    }))
  }

  render() {
    if (this.state.hasError) {
      return (
        <PageLayout variant="fullBleed" className="bg-twin-chat-bg">
          <div
            className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 text-center"
            data-testid="chat-page-crash"
          >
            <AlertCircle className="h-6 w-6 text-destructive" />
            <Heading level={2}>{i18n.t('chatPage.crashTitle', { ns: 'app' })}</Heading>
            <Text size="sm" color="muted">
              {i18n.t('chatPage.crashBody', { ns: 'app' })}
            </Text>
            <Button
              type="button"
              data-testid="chat-page-crash-retry"
              onClick={this.handleRetry}
            >
              {i18n.t('chatPage.tryAgain', { ns: 'app' })}
            </Button>
          </div>
        </PageLayout>
      )
    }

    return <div key={this.state.resetKey}>{this.props.children}</div>
  }
}
